import {
  EyeOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Drawer,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ReactComponent as Archiveicon } from '../../Images/Archiveicon.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { ReactComponent as Archive } from '../../Images/archive.svg';
import {
  addDieSubCategory,
  deleteDieSubCategory,
  editDieSubCategory,
  fetchDieSubCategoryList,
  resetDieSubCategoryList,
} from '../../slices/die-subcategory';
import dieSubCategoryService from '../../services/die-subcategorylist.service';
import ImportDataModal from '../../Common/ImportDataModal';
import moment from 'moment';
import { debounce } from 'lodash';
import { fetchAndSetIpAddress } from '../../slices/ipSlice';
import { createActivityLog } from '../../slices/activity-log';
import logo from '../../Images/alux-logo.png';
import formatFile from "../../Images/sample-files/section-sub-category-format.xlsx";
import ViewDieSubCategory from './ViewDieSubCategory';

// Audit columns visibility fix
export default function DieSubCategoryList() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [pagesize, setPageSize] = useState(100);
  const [orderBy, setOrderBy] = useState('');
  const [isEditing, setIsEditing] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [currentSubCategory, setCurrentSubCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [formErrorMessage, setFormErrorMessage] = useState('');
  const { user: currentUser } = useSelector(state => state.auth);
  const [search, setSearch] = useState(localStorage.getItem('die_text_search_sub_category') || '');
  const ipAddress = useSelector(state => state.ip.ipAddress);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    if (!ipAddress) {
      dispatch(fetchAndSetIpAddress());
    }
  }, [ipAddress, dispatch]);
  
  const [checkedList, setCheckedList] = useState([
    'SrNo',
    'name',
    'description',
    'created_at',
    'created_by',
    'updated_at',
    'updated_by',
    'action',
  ]);
  const { data, totalRecord, totaldie, status } = useSelector(
    state => state.dieSubCategory,
  );
  useEffect(() => {
    dispatch(
      fetchDieSubCategoryList({
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
  }, [page, pagesize, search, orderBy, dispatch]);

  const handleError = error => {
    if (typeof error === 'object' && error !== null) {
      const messages = [];
      for (const key in error) {
        if (Array.isArray(error[key])) {
          messages.push(`${key}: ${error[key].join(', ')}`);
        }
      }
      if (messages.length > 0) {
        setFormErrorMessage(messages.join('; '));
      } else {
        setFormErrorMessage('An unknown error occurred.');
      }
    } else if (typeof error === 'string') {
      setFormErrorMessage(error);
    } else if (error instanceof Error) {
      setFormErrorMessage(error.message);
    } else {
      setFormErrorMessage('An unknown error occurred.');
    }
  };
  const handleAdd = async values => {
    try {
      setFormErrorMessage(null); // Clear previous error message before submission
      values.deleted = 0;

      await dispatch(addDieSubCategory(values)).unwrap();
      message.success('Section Sub Category added successfully!');
      dispatch(fetchDieSubCategoryList({ page: 1, pagesize: 10, search }));

      form.resetFields();
      setOpen(false);
    } catch (error) {
      if (
        error?.response?.data?.name?.[0] ===
        'Enter unique value. This name already exists.'
      ) {
        setFormErrorMessage(
          'This subcategory name already exists. Please enter a unique name.',
        );
      } else {
        handleError(error);
      }
    }
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };
   const refreshTable = () => {
      dispatch(resetDieSubCategoryList());
      dispatch(
        fetchDieSubCategoryList({
          page: 1,
          pagesize,
          ...(search && { search }),
          ...(orderBy && { ordering: orderBy }),
        })
      );
    };
  
  const handleEdit = async values => {
    if (currentSubCategory) {
      try {
        setFormErrorMessage(null); // Clear previous error message before submission

        await dispatch(
          editDieSubCategory({ id: currentSubCategory.id, updatedData: values }),
        ).unwrap();
        message.success('Section Sub Category updated successfully!');
        dispatch(
          fetchDieSubCategoryList({ page, pagesize, ...(search && { search }) }),
        );

        form.resetFields();
        setOpen(false);
        setIsEditing(false);
        setCurrentSubCategory(null);
      } catch (error) {
        if (
          error?.response?.data?.name?.[0] ===
          'Enter unique value. This name already exists.'
        ) {
          setFormErrorMessage(
            'This subcategory name already exists. Please enter a unique name.',
          );
        } else {
          handleError(error);
        }
      }
    }
  };

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    form.resetFields();
    setFormErrorMessage(null);
  };

  
  const onEdit = record => {
    setIsEditing(true);
    setCurrentSubCategory(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description, // Set the description field as well
    });
    setOpen(true);
  };

  const handleView = record => {
    setSelectedSubCategory(record);
    setViewOpen(true);
  };

  const onViewClose = () => {
    setViewOpen(false);
    setSelectedSubCategory(null);
  };
  const resetFilter = () => {
    setSearch('');
    setOrderBy(null); // if applicable
    localStorage.removeItem('die_text_search_sub_category');
    localStorage.removeItem('die_group_search_sub_category');
    dispatch(fetchDieSubCategoryList({ page: 1, pagesize }));
  };

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';

    if (sortField === 'name' && sortOrder === 'ascend') {
      sortingEnumKey = 'name';
    }
    if (sortField === 'name' && sortOrder === 'descend') {
      sortingEnumKey = '-name';
    }
    return sortingEnumKey;
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    if (pagination?.pageSize) setPageSize(pagination.pageSize);
    if (pagination?.current) setPage(pagination.current);
  };
  const handleSearch = (e) => {
    const value = e.target.value;
    debouncedAPICall(value)
    setSearch(value);
    localStorage.setItem('die_text_search_sub_category', value); // save to localStorage

  };

  const debouncedAPICall = useMemo(() =>
    debounce((query) => {
      if (query.length >= 3) {
        dispatch(fetchDieSubCategoryList({ search: query }));
        localStorage.setItem('die_text_search_sub_category', query);
      }
    }, 50), [dispatch]
  );

  useEffect(() => {
    return () => {
      debouncedAPICall.cancel(); // Clean up on unmount
    };
  }, [debouncedAPICall]);

  const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };

  const handleDelete = id => {
    if (!id) {
      message.error('Missing ID for deletion!');

      return;
    }

    dispatch(deleteDieSubCategory(id))
      .unwrap()
      .then(() => {
        message.success('Section Sub Category Archive Successfully!');
        setSelectedRowKeys([]);
        dispatch(
          fetchDieSubCategoryList({
            page,
            pagesize,
            ...(search && { search: search }),
          }),
        );
      })
      .catch(error => {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }

          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        } else if (typeof error === 'string') {
          message.error(error); // Handle string error
        } else if (error instanceof Error) {
          message.error(error.message); // Handle Error object
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };
  const handleBulkArchive = async () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      message.error('Please select at least one record to archive');
      return;
    }

    try {
      await dieSubCategoryService.bulkArchiveDieSubCategory(selectedRowKeys);
      message.success(
        `${selectedRowKeys.length} Section Sub Category(s) archived successfully`,
      );
      setSelectedRowKeys([]);
      dispatch(
        fetchDieSubCategoryList({
          page,
          pagesize,
          ...(search && { search: search }),
          
        }),
      );
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to archive section sub categories';
      message.error(errorMessage);
    }
  };
  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1, // Calculate Sr. No. based on page and limit
  }));

  const handleColumnVisibilityChange = checkedValues => {
    setCheckedList(checkedValues);
  };

  const allColumns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      width: 100,
      sorter: false,
    },
    {
      title: 'Sub Category Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 300,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      sorter: true,
      render: record => {
        return (
          <span>
            {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      sorter: true,
      width: 100,
      render: record => {
        return (
          <span>
            {record && typeof record === 'object'
              ? `${record.first_name || ''} ${record.last_name || ''}`.trim() || 'N/A'
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 100,
      sorter: true,
      render: record => {
        return (
          <span>
            {record
              ? dayjs(record).format('DD-MM-YYYY HH:mm')
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      width: 100,
      sorter: true,
      render: record => {
        return (
          <span>
            {record && typeof record === 'object'
              ? `${record.first_name || ''} ${record.last_name || ''}`.trim() || 'N/A'
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      width: 100,
      fixed: 'right',
      key: 'action',
      render: (text, record) => (
        <Space>
          {currentUser.permissions.permissions.die.includes(
            'view_diesubcategory',
          ) && (
            <Tooltip placement="top" title="View">
              <span
                onClick={() => handleView(record)}
                style={{ cursor: 'pointer' }}
              >
                <EyeOutlined style={{ fontSize: '17px' }} />
              </span>
            </Tooltip>
          )}
          {currentUser.permissions.permissions.die.includes(
            'change_diesubcategory',
          ) && (
              <Link onClick={() => onEdit(record)}>
                <Tooltip placement="top" title="Edit">
                  <EditIcon />
                </Tooltip>
              </Link>
            )}
          {currentUser.permissions.permissions.die.includes(
            'delete_diesubcategory',
          ) && (
              <Link>
                <Tooltip placement="top" title="Archive">
                  <Popconfirm
                    title="Are you sure you want to archive this record?"
                    onConfirm={() => handleDelete(record.id)} // Trigger delete on confirmation
                    onCancel={() => message.info('Action cancelled')} // Optional cancel action
                    okText="Yes"
                    cancelText="No"
                  >
                    <Archive style={{ cursor: 'pointer' }} />
                  </Popconfirm>
                </Tooltip>
              </Link>
            )}
        </Space>
      ),
    },
  ];

  const filteredColumns = allColumns.filter(column =>
    checkedList.includes(column.key),
  );
  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item key="columnMenu">
        <Checkbox.Group
          value={checkedList}
          onChange={handleColumnVisibilityChange}
        >
          <Row>
            <Col xs={24} md={12}>
              <Checkbox value="SrNo" disabled>
                Sr. No.
              </Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="name">Sub Category Name</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_at">Created At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_by"> Created By </Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_at">Updated At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_by">Updated By</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="action" disabled>
                {' '}
                Action{' '}
              </Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );
  const allCsvColumns = [
    { header: 'Sr. No.', dataKey: 'SrNo', field: 'SrNo' },
    { header: 'Sub Category Name', dataKey: 'name', field: 'name' },
    { header: 'Created At', dataKey: 'createdAt', field: 'created_at' },
    { header: 'Created By', dataKey: 'createdBy', field: 'created_by' },
    { header: 'Updated At', dataKey: 'updatedAt', field: 'updated_at' },
    { header: 'Updated By', dataKey: 'updatedBy', field: 'updated_by' },
  ];
  const csvHeaders = allCsvColumns
    .filter(col => checkedList.includes(col.field)) // Filter only selected columns
    .map(col => ({
      label: col.header, // CSV column header
      key: col.dataKey, // Corresponding data key
    }));
  const csvListData = dataSource.map((item, index) => {
    const row = {
      SrNo: index + 1, // Add serial number as the first column
    };
    allCsvColumns
      .filter(col => checkedList.includes(col.field))
      .forEach(col => {
        switch (col.field) {
          case 'SrNo':
            row.SrNo = item.SrNo || 'N/A';
            break;
          case 'name':
            row.name = item.name || 'N/A';
            break;
          case 'created_at':
            row.createdAt = item.created_at
              ? moment(item.created_at).format('DD-MM-YYYY')
              : 'N/A';
            break;
          case 'created_by':
            row.createdBy =
              item.created_by && typeof item.created_by === 'object'
                ? `${item.created_by.first_name || ''} ${
                    item.created_by.last_name || ''
                  }`.trim() || 'N/A'
                : 'N/A';
            break;
          case 'updated_at':
            row.updatedAt = item.updated_at
              ? moment(item.updated_at).format('DD-MM-YYYY HH:mm')
              : 'N/A';
            break;
          case 'updated_by':
            row.updatedBy =
              item.updated_by && typeof item.updated_by === 'object'
                ? `${item.updated_by.first_name || ''} ${
                    item.updated_by.last_name || ''
                  }`.trim() || 'N/A'
                : 'N/A';
            break;
          default:
            row[col.dataKey] = 'N/A';
        }
      });
    return row;
  });

  const handlePdfDownload = async () => {
    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    pdf.addImage(logo, "PNG", pageWidth - 100, 10, 90, 30);

    pdf.setFontSize(14);
    pdf.text("Section Sub Category", pageWidth / 2, 40, { align: "center" });

    const pdfColumns = [
      { header: "Sr. No.", dataKey: "SrNo", field: "SrNo" },
      { header: "Sub Category Name", dataKey: "name", field: "name" },
      { header: "Created At", dataKey: "createdAt", field: "created_at" },
      { header: "Created By", dataKey: "createdBy", field: "created_by" },
      { header: "Updated At", dataKey: "updatedAt", field: "updated_at" },
      { header: "Updated By", dataKey: "updatedBy", field: "updated_by" },
    ];

    const columns = pdfColumns.filter(col => checkedList.includes(col.field));

    const rows = dataSource.map(item => {
      const row = {};
      columns.forEach(col => {
        switch (col.field) {
          case "SrNo":
            row.SrNo = item.SrNo || "N/A";
            break;
          case "name":
            row.name = item.name || "N/A";
            break;
          case "created_at":
            row.createdAt = item.created_at
              ? moment(item.created_at).format("DD-MM-YYYY")
              : "N/A";
            break;
          case "created_by":
            row.createdBy =
              item.created_by && typeof item.created_by === "object"
                ? `${item.created_by.first_name || ""} ${
                    item.created_by.last_name || ""
                  }`.trim() || "N/A"
                : "N/A";
            break;
          case "updated_at":
            row.updatedAt = item.updated_at
              ? moment(item.updated_at).format("DD-MM-YYYY HH:mm")
              : "N/A";
            break;
          case "updated_by":
            row.updatedBy =
              item.updated_by && typeof item.updated_by === "object"
                ? `${item.updated_by.first_name || ""} ${
                    item.updated_by.last_name || ""
                  }`.trim() || "N/A"
                : "N/A";
            break;
          default:
            break;
        }
      });
      return row;
    });

    pdf.autoTable({
      columns,
      body: rows,
      startY: 80,
      theme: "grid",
      styles: {
        font: "times",
        halign: "center",
        overflow: "linebreak",
        cellPadding: 3.5,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      headStyles: {
        textColor: [0, 0, 0],
        fontStyle: "bold",
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fillColor: false,
      },
      rowStyles: {
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      },
      tableLineColor: [0, 0, 0],
      rowPageBreak: "avoid",

      // ✅ Footer (date left, page no right)
      didDrawPage: data => {
        const pageCount = pdf.internal.getNumberOfPages();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const pageWidth = pdf.internal.pageSize.getWidth();

        pdf.setFontSize(8);
        pdf.setTextColor(100);

        // Left Footer → Date & Time
        const now = moment();
        const dateStr = `${now.format("DD-MM-YYYY hh:mm:ss A")}`;
        pdf.text(dateStr, 40, pageHeight - 20);

        // Right Footer → Page number
        const pageStr = `Page ${data.pageNumber} of ${pageCount}`;
        pdf.text(pageStr, pageWidth - 40, pageHeight - 20, { align: "right" });
      },
    });

    pdf.save(`section_sub_category${+new Date()}`);

    dispatch(
      createActivityLog({
        action: "DOWNLOAD",
        module_name: "Profile SubCategory",
        ip_address: ipAddress,
        discription: "Profile subcategory pdf download successfully!",
      })
    );
  };



  const activityLog = async () => {
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Profile SubCategory',
        ip_address: ipAddress,
        discription: 'Profile subcategory excel download successfully!',
      }),
    );
  }

  return (
    <>
    {/* // <div className='mainwrap'> */}
      <div className="sticyaction">
        <Row className="pagenamerow mb-0" justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>Section Sub Categories</h2>
            <div className="bredcrumbwrp">
              <Link to="/die-profilelist" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  //{ title: <Link to="/die-profilelist">Section</Link> },
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: 'Section Sub Categories' },
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="small">
              
           
              {currentUser.permissions.permissions.die.includes(
                'add_diesubcategory',
              ) && (
                  <Button
                    type="primary"
                    onClick={showDrawer}
                    icon={<PlusOutlined />}
                  >
                    Add Section Sub Category
                  </Button>
                )}
                 {selectedRowKeys.length > 0  && (
                <Popconfirm
                  title={`Are you sure you want to archive ${selectedRowKeys.length} selected section sub category(s)?`}
                  onConfirm={handleBulkArchive}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="primary" danger>
                    Bulk Archive ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              )}
                  <Button type="primary" icon={<UploadOutlined  />} onClick={() => setOpenModal(true)}>
                                Import Data
                              </Button>
              {currentUser.permissions.permissions.die.includes(
                'delete_diesubcategory',
              ) && (
                  <Tooltip placement="top" title="Archive">
                    <Link to="/archive-die-subcategories" className="iconlink">
                      {' '}
                      <Archiveicon style={{ cursor: 'pointer' }} />
                    </Link>
                  </Tooltip>
                )}
                   {currentUser.permissions.permissions.die.includes(
                'download_profile_sub_category_excel_copy',
              ) && (
                  <Tooltip placement="top" title={'Export Excel'}>
                    <Button
                      type="primary"
                      className="iconlink"
                      style={{ width: '10px' }}
                      onClick={activityLog}
                    >
                      <CSVLink
                        filename={'Section SubCategories.csv'}
                        className="iconlink"
                        style={{ width: '10px' }}
                        data={csvListData}
                        headers={csvHeaders}
                      >
                        <FileExcelOutlined />
                      </CSVLink>
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.die.includes(
                'download_profile_sub_category_pdf_copy',
              ) && (
                  <Tooltip placement="top" title={'Export PDF'}>
                    <Button
                      type="primary"
                      className="iconlink"
                      onClick={handlePdfDownload}
                    >
                      <FilePdfOutlined />
                    </Button>
                  </Tooltip>
                )}
              <Dropdown
                overlay={columnMenu}
                visible={dropdownVisible}
                onVisibleChange={setDropdownVisible}
              >
                <Button
                  type="primary"
                  icon={<ColumnList />}
                  onClick={e => e.preventDefault()}
                />
              </Dropdown>
            </Space>
          </Col>
        </Row>
      </div>
      <div className="formwrapper">
        <Row gutter={16} className="mb-3">
          <Col xs={24} sm={12} md={8} lg={6}>
      <Input
                placeholder="Search by name"
              
                prefix={<SearchOutlined />}
                value={search}
                
                onChange={handleSearch}
              />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
              <Tooltip placement="top" title="Reset Filter">
                <Button

                  onClick={resetFilter}
                >
               Reset
                </Button>
              </Tooltip>
              </Col>
              </Row>
              </div>
      
     
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>
      <div className='tablewrp'>
        <Table
          rowKey="id"
          // rowSelection={rowSelection}
          rowSelection={ rowSelection }
          dataSource={dataSource}
          columns={filteredColumns}
          pagination={false} // Disable built-in pagination
          loading={status === 'loading'}
          onChange={handleTableChange}
          scroll={{ x: '600px' }}
        />
      </div>
      </div>
      <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            background: '#fff',
            padding: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
      <Pagination
        current={page}
        pageSize={pagesize}
        total={totalRecord}
        showSizeChanger
        onShowSizeChange={handlePageSizeChange}
        pageSizeOptions={["20", "50", "100"]}
        showTotal={(total) => (
          <Row gutter={16}>
            <Col>
              <span style={{
                backgroundColor: '#ffeaea', // light red background
                padding: '2px 10px',
                borderRadius: '8px',
                fontWeight: 'bold',
                display: 'inline-block',
              }}>
                Total Number of Profile: {totaldie} items
              </span>
            </Col>
            <Col>
              <span>Total: {total || 0} items</span>
            </Col>
          </Row>
        )}
        onChange={handlePageChange}
        locale={{ items_per_page: 'Items / Page' }}
      />

</div>
</div>
      <Drawer
        title={isEditing ? 'Edit Section SubCategory' : 'Add Section Sub Category'}
        width={400}
        onClose={onClose}
        open={open}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ name: '', description: '' }}
          onFinish={isEditing ? handleEdit : handleAdd}
        >
          <Form.Item
            name="name"
            label="Sub Category Name"
            rules={[
              { required: true, message: 'Enter a valid Sub category name!' },

              {
                max: 49,
                message: 'Category Name cannot be longer than 50 characters ',
              },
            ]}
            help={formErrorMessage || undefined}
            validateStatus={formErrorMessage ? 'error' : undefined}
          >
            <Input
              maxLength={50}
              placeholder="Enter Sub Category Name"
              onChange={e => {
                const input = e.target;
                const value = input.value;

                const caretPosition = input.selectionStart;

                const formattedValue = value
                  .toLowerCase()
                  .replace(/\b\w/g, char => char.toUpperCase());
                form.setFieldsValue({ name: formattedValue });

                setTimeout(() => {
                  input.setSelectionRange(caretPosition, caretPosition);
                }, 0);
              }}
            />
          </Form.Item>

          <div className="flex justify-end">
            <Button type="primary" icon={<PlusOutlined />} htmlType="submit">
              {isEditing ? 'Update Sub Category' : 'Add Sub Category'}
            </Button>
          </div>
        </Form>
      </Drawer>

      {viewOpen && (
        <ViewDieSubCategory
          open={viewOpen}
          onClose={onViewClose}
          dieSubCategory={selectedSubCategory}
        />
      )}
    {/* // </div> */}
    <ImportDataModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        refreshTable={refreshTable}
        formatFilePath={formatFile}
        // model={"sectionsubcategory"}
        model="die-subcategory"
      />
    {/* </div> */}

    </>
  );
}
